import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  fetchProducts,
  ProductListItem,
} from "@/modules/productStorefront/features/ProductManagement/api/productManagementApi";

interface ProductListState {
  products: ProductListItem[];
  totalCount: number;
  fetchLoading: boolean;
  fetchError: string | null;
}

const initialState: ProductListState = {
  products: [],
  totalCount: 0,
  fetchLoading: false,
  fetchError: null,
};

export const productListSlice = createSlice({
  name: "productList",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setProducts(state, action: PayloadAction<ProductListItem[]>) {
      state.products = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    removeProduct(state, action: PayloadAction<string>) {
      state.products = state.products.filter(
        (product) => product.uuid !== action.payload,
      );
      state.totalCount -= 1;
    },
    updateProductPublishedStatus(
      state,
      action: PayloadAction<{ uuid: string; isPublished: boolean }>,
    ) {
      const { uuid, isPublished } = action.payload;
      const index = state.products.findIndex((p) => p.uuid === uuid);
      if (index !== -1) {
        state.products[index].isPublished = isPublished;
      }
    },
  },
});

export default productListSlice.reducer;

export const {
  setFetchLoading,
  setFetchError,
  setProducts,
  setTotalCount,
  removeProduct,
  updateProductPublishedStatus,
} = productListSlice.actions;

export const fetchProductListAction =
  (params: Record<string, any>): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const result = await fetchProducts(params);
      dispatch(setProducts(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch products.";
      dispatch(setFetchError(message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };
